import { useEffect, useId, useRef, useState } from "react";
import { MessageBlocks } from "@ferrivia/chat-ui";
import {
  ACTION_CHANGE_REASON_MAX,
  ACTION_INSTRUCTIONS_MAX,
  ACTION_MAX_WEEKS,
  ACTION_TITLE_MAX,
  ApiError,
  type ActionKind,
  type CheckInChannel,
  type PatientAction,
  type PatientActionCreate,
  type ReadMessageBody,
} from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, RadioChip, Textarea, TextInput, ToggleChip } from "@ferrivia/ui";
import { ACTION_KINDS, CHECK_IN_CHANNELS, KIND_ICON, useActionLabels } from "../../actions/kinds";
import { checkInDateFromWeeks, toIsoDate, type CurrentCheckIn } from "../../actions/plan";
import type { StaffProfileState } from "../../hooks/useStaffProfile";
import { ActionDrawerShell } from "./ActionDrawerShell";
import s from "./ActionDrawer.module.css";

export interface ActionSourceMessage {
  id: string;
  senderName: string;
  occurredAt: string;
  body: ReadMessageBody;
}

export interface ActionDrawerProps {
  container: HTMLElement | null;
  onClose: () => void;
  /** Where the drawer was opened from, for the open event. */
  origin: "message" | "plan" | "change";
  clientName: string;
  staff: StaffProfileState;
  /** The message being turned into an action; absent when opened from the strip. */
  sourceMessage?: ActionSourceMessage;
  /** Change mode: the action being replaced. Its fields are copied as the clinician's own earlier input. */
  replaces?: PatientAction;
  replacesCheckIn?: CurrentCheckIn;
  onSave: (input: PatientActionCreate) => Promise<unknown>;
}

function parseWeeks(text: string): number | null {
  const n = Number(text);
  return text.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= ACTION_MAX_WEEKS ? n : null;
}

export function ActionDrawer({
  container,
  onClose,
  origin,
  clientName,
  staff,
  sourceMessage,
  replaces,
  replacesCheckIn,
  onSave,
}: ActionDrawerProps) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const { kindLabel, channelLabel, titleLabel } = useActionLabels();
  const tracker = useTracker();
  const uid = useId();
  const [today] = useState(() => new Date());

  const [kind, setKind] = useState<ActionKind | null>(replaces?.kind ?? null);
  const [title, setTitle] = useState(replaces?.title ?? "");
  const [instructions, setInstructions] = useState(replaces?.instructions ?? "");
  const [ongoing, setOngoing] = useState(replaces ? replaces.runsForWeeks === null : false);
  const [weeksText, setWeeksText] = useState(replaces?.runsForWeeks != null ? String(replaces.runsForWeeks) : "");
  const [checkInOn, setCheckInOn] = useState(replacesCheckIn?.on ?? "");
  const [dateEdited, setDateEdited] = useState(Boolean(replacesCheckIn?.on));
  const [channel, setChannel] = useState<CheckInChannel | null>(replacesCheckIn?.channel ?? null);
  const [clientVisible, setClientVisible] = useState(replaces?.clientVisible ?? true);
  const [changeReason, setChangeReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const trackerRef = useRef(tracker);
  const titleRef = useRef<HTMLInputElement>(null);
  trackerRef.current = tracker;
  useEffect(() => {
    trackerRef.current.track("clinic.action_drawer_opened", { source: origin });
  }, [origin]);

  const weeks = ongoing ? null : parseWeeks(weeksText);
  const runsValid = ongoing || weeks !== null;
  const dateWithoutChannel = checkInOn !== "" && channel === null;
  const channelWithoutDate = checkInOn === "" && channel !== null;
  const profile = staff.status === "success" ? staff.profile : null;
  const titleMissing = profile !== null && profile.title === null;
  const canSave =
    kind !== null &&
    title.trim() !== "" &&
    runsValid &&
    !dateWithoutChannel &&
    !channelWithoutDate &&
    staff.status !== "loading" &&
    !titleMissing &&
    !saving;

  function onWeeksChange(value: string) {
    setWeeksText(value);
    setOngoing(false);
    if (!dateEdited) {
      const n = parseWeeks(value);
      setCheckInOn(n === null ? "" : checkInDateFromWeeks(today, n));
    }
  }

  function onOngoingChange(checked: boolean) {
    setOngoing(checked);
    if (checked) {
      setWeeksText("");
      if (!dateEdited) {
        setCheckInOn("");
      }
    }
  }

  function onDateChange(value: string) {
    setCheckInOn(value);
    setDateEdited(value !== "");
  }

  async function save() {
    if (!canSave || kind === null) {
      return;
    }
    const input: PatientActionCreate = {
      kind,
      title: title.trim(),
      runsForWeeks: ongoing ? null : weeks,
      clientVisible,
      ...(instructions.trim() !== "" ? { instructions: instructions.trim() } : {}),
      ...(checkInOn !== "" && channel !== null ? { checkInOn, checkInChannel: channel } : {}),
      ...(sourceMessage
        ? { sourceMessageId: sourceMessage.id }
        : replaces?.sourceMessageId
          ? { sourceMessageId: replaces.sourceMessageId }
          : {}),
      ...(replaces ? { replacesId: replaces.id } : {}),
      ...(replaces && changeReason.trim() !== "" ? { changeReason: changeReason.trim() } : {}),
    };
    setSaving(true);
    setErrorKey(null);
    try {
      await onSave(input);
      onClose();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "network";
      trackerRef.current.track("clinic.action_save_failed", { code });
      setErrorKey(code);
      setSaving(false);
    }
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void save();
    }
  }

  const errorText =
    errorKey === null
      ? null
      : errorKey === "staff_profile_incomplete"
        ? t("actionDrawer.errorProfile", "Your professional title isn't set — ask Ville.")
        : errorKey === "invalid_source_message"
          ? t("actionDrawer.errorSource", "That message can't be linked to an action.")
          : errorKey === "invalid_check_in_date"
            ? t("actionDrawer.errorDate", "Pick a check-in date that hasn't passed.")
            : errorKey === "action_not_active"
              ? t("actionDrawer.errorNotActive", "This action has already ended or been changed.")
              : t("actionDrawer.errorFailed", "Couldn't save. Try again.");

  const heading = replaces
    ? t("actionDrawer.titleChange", "Change action")
    : t("actionDrawer.titleCreate", "New action");

  const quoteWhen = sourceMessage
    ? new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(
        new Date(sourceMessage.occurredAt),
      )
    : null;

  const footer = (
    <>
      {titleMissing ? (
        <p className={s.blocked} role="alert">
          {t("actionDrawer.titleMissing", "Your professional title isn't set — ask Ville.")}
        </p>
      ) : null}
      {errorText ? (
        <p className={s.error} role="alert">
          {errorText}
        </p>
      ) : null}
      {profile?.name && profile.title ? (
        <span className={s.recordedAs}>
          {t("actionDrawer.recordedAs", "Recorded as")} <b>{`${profile.name}, ${titleLabel(profile.title)}`}</b>
        </span>
      ) : null}
      <div className={s.footButtons}>
        <Button variant="secondary" onClick={onClose}>
          {t("actionDrawer.cancel", "Cancel")}
        </Button>
        <Button onClick={() => void save()} disabled={!canSave}>
          {t("actionDrawer.save", "Save action")}
        </Button>
      </div>
    </>
  );

  return (
    <ActionDrawerShell
      container={container}
      title={heading}
      onClose={onClose}
      footer={footer}
      onKeyDown={onKeyDown}
      initialFocusRef={titleRef}
    >
      {sourceMessage ? (
        <blockquote className={s.quote} style={{ margin: 0 }}>
          <div className={s.quoteWho}>
            {sourceMessage.senderName} <span className={s.quoteWhen}>· {quoteWhen}</span>
          </div>
          <MessageBlocks body={sourceMessage.body} />
        </blockquote>
      ) : null}

      <div className={s.field}>
        <span id={`${uid}-kind`} className={s.label}>
          {t("actionDrawer.kind", "Kind")}
        </span>
        <div role="radiogroup" aria-labelledby={`${uid}-kind`} className={s.kinds}>
          {ACTION_KINDS.map((option) => {
            const Icon = KIND_ICON[option];
            return (
              <RadioChip
                key={option}
                name={`${uid}-kind`}
                className={s.kindChip}
                checked={kind === option}
                onChange={() => setKind(option)}
              >
                <Icon aria-hidden size={18} />
                {kindLabel(option)}
              </RadioChip>
            );
          })}
        </div>
      </div>

      <div className={s.field}>
        <label htmlFor={`${uid}-title`} className={s.label}>
          {t("actionDrawer.whatSees", { name: clientName, defaultValue: "What {{name}} sees" })}
        </label>
        <TextInput
          id={`${uid}-title`}
          ref={titleRef}
          value={title}
          maxLength={ACTION_TITLE_MAX}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className={s.field}>
        <label htmlFor={`${uid}-instructions`} className={s.label}>
          {t("actionDrawer.instructions", "Instructions")}
        </label>
        <Textarea
          id={`${uid}-instructions`}
          value={instructions}
          rows={3}
          maxHeight={200}
          maxLength={ACTION_INSTRUCTIONS_MAX}
          onChange={(event) => setInstructions(event.target.value)}
        />
      </div>

      <div className={s.field}>
        <span id={`${uid}-runs`} className={s.label}>
          {t("actionDrawer.runsFor", "Runs for")}
        </span>
        <div role="group" aria-labelledby={`${uid}-runs`} className={s.chips}>
          <span className={s.inline}>
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              max={ACTION_MAX_WEEKS}
              className={s.weeks}
              aria-label={t("actionDrawer.weeksInput", "Weeks")}
              value={weeksText}
              onChange={(event) => onWeeksChange(event.target.value)}
            />
            {t("actionDrawer.weeksUnit", "weeks")}
          </span>
          <ToggleChip checked={ongoing} onChange={(event) => onOngoingChange(event.target.checked)}>
            {t("actions.ongoing", "Ongoing")}
          </ToggleChip>
        </div>
      </div>

      <div className={s.field}>
        <span id={`${uid}-checkin`} className={s.label}>
          {t("actionDrawer.checkIn", "Check-in")}
        </span>
        <TextInput
          type="date"
          aria-labelledby={`${uid}-checkin`}
          value={checkInOn}
          min={toIsoDate(today)}
          onChange={(event) => onDateChange(event.target.value)}
        />
        {checkInOn !== "" && !dateEdited && weeks !== null ? (
          <p className={s.hint}>
            {t("actionDrawer.dateHint", { count: weeks, defaultValue: "today + {{count}} weeks — edit freely" })}
          </p>
        ) : null}
        <div role="radiogroup" aria-label={t("actionDrawer.channel", "Check-in channel")} className={s.chips}>
          {CHECK_IN_CHANNELS.map((option) => (
            <RadioChip
              key={option}
              name={`${uid}-channel`}
              checked={channel === option}
              onChange={() => setChannel(option)}
            >
              {channelLabel(option)}
            </RadioChip>
          ))}
        </div>
        {dateWithoutChannel ? (
          <p className={s.hint}>{t("actionDrawer.channelNeeded", "Choose how the check-in happens.")}</p>
        ) : null}
        {channelWithoutDate ? (
          <p className={s.hint}>{t("actionDrawer.dateNeeded", "Pick a check-in date, or clear the channel.")}</p>
        ) : null}
      </div>

      <div className={s.toggleRow}>
        <button
          type="button"
          role="switch"
          aria-checked={clientVisible}
          aria-labelledby={`${uid}-visible`}
          aria-describedby={`${uid}-visible-hint`}
          className={s.switch}
          onClick={() => setClientVisible((v) => !v)}
        />
        <span className={s.toggleText}>
          <span id={`${uid}-visible`}>
            {t("actionDrawer.showInApp", { name: clientName, defaultValue: "Show in {{name}}'s app" })}
          </span>
          <span id={`${uid}-visible-hint`} className={s.hint}>
            {profile?.name && profile.title
              ? t("actionDrawer.showInAppHint", {
                  name: clientName,
                  author: profile.name,
                  title: titleLabel(profile.title),
                  defaultValue: "On {{name}}'s home screen as “Recommended by {{author}}, {{title}}”.",
                })
              : t("actionDrawer.showInAppHintPlain", {
                  name: clientName,
                  defaultValue: "On {{name}}'s home screen.",
                })}
          </span>
        </span>
      </div>

      {replaces ? (
        <div className={s.field}>
          <label htmlFor={`${uid}-reason`} className={s.label}>
            {t("actionDrawer.changeReason", "Reason for change")}
          </label>
          <Textarea
            id={`${uid}-reason`}
            value={changeReason}
            rows={2}
            maxHeight={160}
            maxLength={ACTION_CHANGE_REASON_MAX}
            onChange={(event) => setChangeReason(event.target.value)}
          />
        </div>
      ) : null}
    </ActionDrawerShell>
  );
}
